import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Car, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VehicleBrowser = ({ onVehicleSelected, compact = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);

  const [selectedRegion, setSelectedRegion] = useState('GCC');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedTrim, setSelectedTrim] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('');

  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    fetchYears();
    fetchMakes();
  }, []);

  useEffect(() => {
    if (selectedYear && selectedMake) {
      fetchModels();
    }
  }, [selectedYear, selectedMake]);

  const fetchYears = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vin/years`);
      setYears(response.data.years || []);
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  };

  const fetchMakes = async () => {
    setLoadingMakes(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vin/makes`);
      const allMakes = response.data.makes || [];

      const popularMakes = ['FORD', 'TOYOTA', 'HONDA', 'CHEVROLET', 'NISSAN', 
                           'BMW', 'MERCEDES-BENZ', 'AUDI', 'VOLKSWAGEN', 'HYUNDAI',
                           'KIA', 'MAZDA', 'SUBARU', 'JEEP', 'RAM', 'GMC', 'LEXUS',
                           'ACURA', 'INFINITI', 'VOLVO', 'LAND ROVER', 'PORSCHE'];

      const filtered = allMakes.filter(make => 
        popularMakes.includes(make.name.toUpperCase())
      );

      setMakes(filtered.length > 0 ? filtered : allMakes.slice(0, 100));
    } catch (error) {
      console.error('Error fetching makes:', error);
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/vin/makes/${selectedMake}/models?year=${selectedYear}`
      );
      setModels(response.data.models || []);
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const canSearch = selectedRegion && selectedYear && selectedMake && selectedModel && selectedTrim && selectedEngine;

  const handleSearch = () => {
    if (!canSearch) return;
    const vehicle = {
      region: selectedRegion,
      year: selectedYear,
      make: selectedMake,
      model: selectedModel,
      trim: selectedTrim,
      engine: selectedEngine,
    };

    // Save selection for detail page badge
    localStorage.setItem('fitmentSelection', JSON.stringify(vehicle));

    if (onVehicleSelected) {
      onVehicleSelected(vehicle);
    } else {
      const params = new URLSearchParams({
        vehicle_region: vehicle.region,
        vehicle_year: vehicle.year,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        vehicle_trim: vehicle.trim,
        vehicle_engine: vehicle.engine,
      });
      navigate(`/products?${params.toString()}`);
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedModel('');
    setModels([]);
  };

  const handleMakeChange = (value) => {
    setSelectedMake(value);
    setSelectedModel('');
    setModels([]);
  };

  if (compact) {
    // Compact: Only show Year/Make/Model to keep homepage small
    return (
      <div className="flex flex-col md:flex-row gap-3">
        <Select value={selectedYear} onValueChange={handleYearChange}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={t('vehicle.year')} />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select 
          value={selectedMake} 
          onValueChange={handleMakeChange}
          disabled={!selectedYear || loadingMakes}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={loadingMakes ? t('vehicle.loading') : t('vehicle.make')} />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {makes.map((make) => (
              <SelectItem key={make.id} value={make.name}>
                {make.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select 
          value={selectedModel} 
          onValueChange={setSelectedModel}
          disabled={!selectedMake || loadingModels}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={loadingModels ? t('vehicle.loading') : t('vehicle.model')} />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {models.map((model) => (
              <SelectItem key={model} value={model}>
                {model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button 
          onClick={() => navigate('/vehicle-selector')}
          disabled={!selectedYear || !selectedMake || !selectedModel}
          className="whitespace-nowrap"
        >
          <Search className="h-4 w-4 mr-2" />
          {t('vehicle.search')}
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" />
          {t('vehicle.browse_by')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Region</Label>
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="USA">USA</SelectItem>
                <SelectItem value="GCC">GCC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('vehicle.year')}</Label>
            <Select value={selectedYear} onValueChange={handleYearChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('vehicle.select_year')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('vehicle.make')}</Label>
            <Select 
              value={selectedMake} 
              onValueChange={handleMakeChange}
              disabled={!selectedYear || loadingMakes}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingMakes ? t('vehicle.loading') : t('vehicle.select_make')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {makes.map((make) => (
                  <SelectItem key={make.id} value={make.name}>
                    {make.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('vehicle.model')}</Label>
            <Select 
              value={selectedModel} 
              onValueChange={setSelectedModel}
              disabled={!selectedMake || loadingModels}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingModels ? t('vehicle.loading') : t('vehicle.select_model')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {models.map((model) => (
                  <SelectItem key={model} value={model}>
                    {model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Trim</Label>
            <Input value={selectedTrim} onChange={(e)=>setSelectedTrim(e.target.value)} placeholder="e.g., SE, Sport" />
          </div>

          <div className="space-y-2">
            <Label>Engine</Label>
            <Input value={selectedEngine} onChange={(e)=>setSelectedEngine(e.target.value)} placeholder="e.g., 2.0L I4" />
          </div>
        </div>

        <Button 
          onClick={handleSearch}
          disabled={!canSearch}
          className="w-full"
          size="lg"
        >
          <Search className="h-5 w-5 mr-2" />
          {t('vehicle.search_compatible')}
        </Button>

        {canSearch && (
          <div className="text-center text-sm text-gray-600 bg-blue-50 p-3 rounded">
            {t('vehicle.selected')} <strong>{selectedYear} {selectedMake} {selectedModel} • {selectedTrim} • {selectedEngine} • {selectedRegion}</strong>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VehicleBrowser;
